import type { CredentialResponse } from "@react-oauth/google";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@replo/design-system/components/toast/Toast";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { userRouter, userUtils } from "@v2/trpc/routes/user";
import { createFormErrorHandler } from "@v2/utils/form-error-handling";
import { storeToken } from "@v2/utils/store-token";
import { useForm } from "react-hook-form";
import { passwordSchema } from "schemas/auth";
import { z } from "zod";

const nameValidationSchema = z.object({
  firstName: z.string().min(1, "You must enter a first name"),
  lastName: z.string(),
});

const passwordValidationSchema = z.object({
  password: z.string().min(1, "Please enter a valid password"),
  newPassword: passwordSchema,
});

export function useProfileForm() {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();

  // Google connect error state
  const [googleErrorMessage, setGoogleErrorMessage] = React.useState<
    string | null
  >(null);

  const {
    register: registerName,
    handleSubmit: handleNameFormSubmit,
    formState: { errors: nameErrors, isDirty: hasNameChanged },
    setError: setNameError,
    reset: resetNameForm,
  } = useForm({
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
    },
    resolver: zodResolver(nameValidationSchema),
  });

  const { mutate: updateProfileName, isPending: isUpdatingName } =
    userRouter.updateProfileName.useMutation({
      onSuccess: (user) => {
        toast.success({
          title: "Profile Updated",
          description: "Your profile has been successfully updated",
        });
        logEvent("user.setting.updated", {
          infoUpdated: "name",
        });
        userUtils.get.setData({}, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            firstName: user.firstName,
            lastName: user.lastName,
            name: `${user.firstName} ${user.lastName}`,
          };
        });
      },
      ...createFormErrorHandler(setNameError),
    });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordFormSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors, dirtyFields: dirtyPasswordFields },
  } = useForm({
    defaultValues: {
      password: "",
      newPassword: "",
    },
    resolver: zodResolver(passwordValidationSchema),
  });

  const { mutate: updateAccountPassword, isPending: isUpdatingPassword } =
    userRouter.updateAccountPassword.useMutation({
      onSuccess: ({ token }) => {
        storeToken(token);
        toast.success({
          title: "Password Updated",
          description: "Your password has successfully been updated.",
        });
        logEvent("user.setting.updated", {
          infoUpdated: "password",
        });
        resetPasswordForm();
      },
    });

  // Google connect mutation
  const { mutate: connectGoogleAccount } =
    userRouter.connectGoogleAccount.useMutation({
      onSuccess: ({ verifiedAt }) => {
        userUtils.get.setData({}, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            verifiedAt,
          };
        });

        toast.success({
          title: "Google Account Connected",
          description: "Your Google account has been successfully connected",
        });
        setGoogleErrorMessage(null);
      },
      onError: (error) => setGoogleErrorMessage(error.message),
    });

  const handleGoogleConnect = async (
    credentialResponse: CredentialResponse,
  ) => {
    const { clientId, credential } = credentialResponse;
    if (clientId && credential) {
      connectGoogleAccount({ clientId, credential });
    }
  };

  const handleNameSubmit = async (
    data: z.infer<typeof nameValidationSchema>,
  ) => {
    updateProfileName(data);
    resetNameForm({}, { keepValues: true });
  };

  const handlePasswordSubmit = async (
    data: z.infer<typeof passwordValidationSchema>,
  ) => {
    updateAccountPassword(data);
  };

  const canUpdatePassword = Boolean(
    dirtyPasswordFields.password && dirtyPasswordFields.newPassword,
  );

  return {
    user,
    // Name form
    registerName,
    handleNameSubmit: (e: React.FormEvent) => {
      return handleNameFormSubmit(handleNameSubmit)(e);
    },
    nameErrors,
    isUpdatingName,
    hasNameChanged,
    // Password form
    registerPassword,
    handlePasswordSubmit: (e: React.FormEvent) => {
      return handlePasswordFormSubmit(handlePasswordSubmit)(e);
    },
    passwordErrors,
    isUpdatingPassword,
    canUpdatePassword,
    // Google connect
    googleErrorMessage,
    handleGoogleConnect,
  };
}
